import React, { createContext, useContext, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { toast } from "sonner";
import api from "./api";
import { getUser, setToken, setUser as persistUser, clearAuth } from "./secureStorage";

const AuthCtx = createContext(null);

// Feb 2026 · Frontend hardcoded ROLE_PERMISSIONS deleted — the app is now
// standardised on the DB-backed matrix served by `/api/rbac/me/permissions`
// (loaded by `RbacProvider` on `lib/rbac.jsx`). `hasPerm()` reads from a
// module-level cache that RbacProvider fills; a legacy shorthand map
// mirrors the backend's so old `"orders:view"` style perm strings still
// resolve against the canonical DB modules (`sales_orders`).
//
// Aug 31, 2026 · Global Alignment · v7 — `hasPerm` is now the SOLE
// source of frontend visibility. Every gate in the app flows through
// this function; the alias map below (`_LEGACY_MODULE_MAP`) resolves
// legacy shorthand codes (`orders`, `dispatch`, `packing`) to their
// sidebar-sync counterparts, so any call site using the old code
// still receives the correct grant when admin ticks the box on the
// Permissions Matrix. Reverse-alias resolution also fires so admin
// legacy grants (e.g. `dispatch:edit`) surface for new callers.
let _permSet = new Set();     // "module_code:action"
let _isAdmin = false;
let _rbacVersion = 0;
const _rbacSubscribers = new Set();
function _subscribeRbac(cb) { _rbacSubscribers.add(cb); return () => _rbacSubscribers.delete(cb); }
function _getRbacVersion() { return _rbacVersion; }
const _LEGACY_MODULE_MAP = {
  // one-to-one shorthand → canonical
  orders:            "sales_orders",
  planning:          "production_planning",
  production:        "weaving",
  packing:           "sales_orders_packing",
  packing_slips:     "sales_orders_slips",
  packing_lists:     "sales_orders_packing",
  delivery_challans: "sales_orders_challans",
  // one-to-many umbrella → any of the split sidebar-sync modules
  dispatch:          ["sales_orders_slips", "sales_orders_packing", "sales_orders_challans"],
};

export function _rbacSetEffective(administrator, permissions) {
  _isAdmin = !!administrator;
  _permSet = new Set((permissions || []).map(p => `${p.module_code}:${p.action}`));
  // Sep 2026 · Reactivity fix — _permSet/_isAdmin live outside React state, so
  // without this, components reading hasPerm() via useAuth() only re-check on
  // their own next unrelated re-render, not when permissions actually arrive
  // (root cause of "ticked in admin, inactive after login" reports). Bumping
  // the version and notifying subscribers forces every useAuth() consumer to
  // re-render the instant RbacProvider.load() resolves.
  _rbacVersion++;
  _rbacSubscribers.forEach(cb => cb());
}

export function hasPerm(_userOrRole, perm) {
  if (_isAdmin) return true;
  if (!perm) return true;
  // Aug 31, 2026 · Support array / OR semantics — callers can pass
  // `hasPerm(["dispatch:edit", "sales_orders_slips:edit"])` and get
  // TRUE when the user holds *any* of them.
  if (Array.isArray(perm)) return perm.some(p => hasPerm(null, p));
  const [mod, act] = String(perm).split(":");
  if (!mod || !act) return false;
  // 1. Direct hit — the incoming module code is what admin ticked.
  if (_permSet.has(`${mod}:${act}`)) return true;
  // 2. Legacy → canonical (call site uses shorthand, admin ticked the sidebar-sync code).
  const canon = _LEGACY_MODULE_MAP[mod];
  if (canon) {
    const targets = Array.isArray(canon) ? canon : [canon];
    if (targets.some(t => _permSet.has(`${t}:${act}`))) return true;
  }
  // 3. Canonical → legacy (call site uses new code, admin ticked the old legacy).
  for (const [legacy, target] of Object.entries(_LEGACY_MODULE_MAP)) {
    const targets = Array.isArray(target) ? target : [target];
    if (targets.includes(mod) && _permSet.has(`${legacy}:${act}`)) return true;
  }
  return false;
}

// Idle-timeout (Feb 2026) — enterprise-grade login hygiene for the plant
// floor. If a signed-in user makes no keyboard, pointer, or scroll input
// for `IDLE_MS`, we clear the session and bounce them to /login. A
// short warning toast fires `WARN_LEAD_MS` before the hard cutoff so the
// planner can save work or wiggle a mouse to stay signed in.
const IDLE_MS = 30 * 60 * 1000;   // 30 minutes
const WARN_LEAD_MS = 60 * 1000;   // 60-second heads-up before logout
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "wheel"];

export function AuthProvider({ children }) {
  // Hydrate from localStorage (secure wrapper) — survives tab hops so the
  // session persists when navigating to/from the public Digital Catalogue.
  const [user, setUser] = useState(() => getUser());
  const [loading, setLoading] = useState(false);
  // Sep 2026 · Subscribes this provider to the RBAC permission store so
  // AuthProvider (and therefore every useAuth() consumer, since the context
  // value is a fresh object each render) re-renders the moment permissions
  // load or change — fixes hasPerm() reading stale/empty state on first
  // paint after login.
  useSyncExternalStore(_subscribeRbac, _getRbacVersion, _getRbacVersion);
  const timeoutRef = useRef(null);
  const warnRef = useRef(null);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.access_token);
    persistUser(data.user);
    setUser(data.user);
    return data.user;
  };

  // Mar 3, 2026 · Round 19 · self-registration removed entirely.
  // The backend `POST /auth/register` endpoint was deleted in Round 18
  // and the frontend has never exposed a Register / Sign-Up affordance.
  // All staff account creation lives on Master Data → Users & Roles
  // (`POST /users/`, Admin-only). Any legacy caller has been
  // audited-away — grep across `/app/frontend/src` returned zero
  // active consumers of a `register` action.

  const logout = () => {
    clearAuth();
    setUser(null);
    window.location.href = "/login";
  };

  // Wire the idle-timeout watchers when a user session is active. Any
  // captured event resets both the warn timer and the hard-logout timer.
  useEffect(() => {
    if (!user) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warnRef.current) clearTimeout(warnRef.current);
      return undefined;
    }
    const clearTimers = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warnRef.current) clearTimeout(warnRef.current);
    };
    const scheduleLogout = () => {
      clearTimers();
      warnRef.current = setTimeout(() => {
        toast.warning("Signing you out in 60 seconds due to inactivity", {
          id: "idle-warn",
          duration: WARN_LEAD_MS,
        });
      }, IDLE_MS - WARN_LEAD_MS);
      timeoutRef.current = setTimeout(() => {
        toast.error("Session ended · signed out for inactivity", { id: "idle-warn" });
        clearAuth();
        setUser(null);
        window.location.href = "/login";
      }, IDLE_MS);
    };
    const onActivity = () => scheduleLogout();
    ACTIVITY_EVENTS.forEach(ev => window.addEventListener(ev, onActivity, { passive: true }));
    scheduleLogout();
    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach(ev => window.removeEventListener(ev, onActivity));
    };
  }, [user]);

  return (
    <AuthCtx.Provider value={{ user, login, logout, loading, hasPerm: (perm) => hasPerm(user, perm) }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
